/**
 * The MyRave Terms of Use — the platform's own terms, served at `/terms` on
 * the bare platform domain (`frontend/worker/terms.ts`, the user's text of
 * 2026-10-21, with the Rideboard terms as its annex).
 *
 * **Every account accepts them** (the user, 2026-10-21: "when a new user
 * registers he also needs to approve the global terms of use, but it's not
 * replacing the approval of the terms of use for the ride, it's additional to
 * it"). The API stores one `platform.terms_acceptance` row per account per
 * version and the completion gate holds an account until the CURRENT version
 * is accepted (`backend/src/modules/users/completion.ts`, requirement
 * `terms`). The Rideboard's own acceptance (`RIDE_TERMS_VERSION`,
 * `travel.terms_acceptance`) is separate and still asked by the Rideboard.
 *
 * **A new version means everybody accepts again.** Change the text on
 * `/terms` in a way anybody agreed to differently → bump this, in the same
 * commit. The page prints "Last updated" from this constant, so the two can
 * never disagree (`frontend/worker/terms.test.ts`).
 */

/** The terms in force. `YYYY-MM-DD` — the day they were last updated. */
export const PLATFORM_TERMS_VERSION = '2026-10-21';

/**
 * What the acceptance screen asks to be ticked, one box per document (the
 * user, 2026-10-21: "2 checkboxes, one for each file"): the Terms of Use and
 * the Privacy Policy, each read in its own frame. The API requires both and
 * stores them with the acceptance as the record of what was agreed.
 */
export const PLATFORM_TERMS_CONFIRMATIONS = ['terms', 'privacy'] as const;
export type PlatformTermsConfirmation = (typeof PLATFORM_TERMS_CONFIRMATIONS)[number];

/**
 * Whether a stored acceptance covers both documents. The screen is two pages
 * and each is kept the moment it is agreed (the user, 2026-10-21: "if the user
 * already signed the first step, no need to sign it again"), so a row may hold
 * `['terms']` alone — not yet accepted. An EMPTY list is a row from the first
 * screen, whose one box covered the terms and the policy together: accepted.
 */
export function platformTermsMet(confirmations: readonly string[]): boolean {
  return confirmations.length === 0 || PLATFORM_TERMS_CONFIRMATIONS.every((k) => confirmations.includes(k));
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/** `2026-10-21` → `21 October 2026` — the date the page prints. */
export function termsVersionDate(version: string = PLATFORM_TERMS_VERSION): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(version);
  if (!m) throw new Error(`not a terms version: ${version}`);
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) throw new Error(`not a terms version: ${version}`);
  return `${Number(m[3])} ${month} ${m[1]}`;
}
